<template>
  <div class="licensesform">
    <div class="row-centered-items number-of-licenses">
      <div>
        <label class="text-septenary pl-3">
          {{ labels.qtdLicenses }}
        </label>
        <br />
        <p
          class="text-small text-red02 pl-3"
          v-if="licenses < activatedUsersCount"
        >
          {{ labels.msgLicensesLessThenUsers01 }}
          <b>{{ activatedUsersCount }}</b>
          {{ labels.msgLicensesLessThenUsers02 }}
          <br />
          <b-link
            class="text-primary text-bold"
            @click="$router.push({ name: 'dashboard.users.edit' })"
          >
            {{ labels.editUsers }}
          </b-link>
        </p>
      </div>
      <input class="form-control licenses" type="number" v-model="licenses" />
    </div>

    <div class="row-centered-items align-baseline pl-3 pr-3">
      <span>
        <b class="text-septenary">{{ labels.labelPricePerUser }}</b>
        <br />
      </span>

      <span class="text-gray03 price-per-user">
        x R$ {{ originalPrice | toCurrency }}
      </span>

      <small class="text-gray03 price-per-user-ps">
        {{ labels.notificationLimitLicenses }}
      </small>
    </div>

    <hr class="pl-3 pr-3" v-if="!!aditionalLicenses && !!partialPrice" />

    <div
      class="row-column pl-3 pr-3"
      v-if="!!aditionalLicenses && !!partialPrice"
    >
      <div class="row-centered-items align-center">
        <b class="text-septenary text-left">
          {{ labels.aditionalLicenses }} <br />
          <small class="text-gray03 price-per-user-ps">
            {{ labels.noticeAditionalLicenses }}
          </small>
        </b>
        <div
          class="resume-aditional-licenses text-gray03 price-per-user text-right"
        >
          <span>{{ aditionalLicenses }} x {{ partialPrice | toCurrency }}</span
          ><br />
          <b class="text-large text-septenary"
            >+ R$ {{ (aditionalLicenses * partialPrice) | toCurrency }}</b
          >
        </div>
      </div>
    </div>

    <hr class="pl-3 pr-3" />

    <div class="row-column pl-3 pr-3">
      <div class="row-centered-items align-baseline">
        <b class="text-septenary text-left">
          {{ labels.totalNextPurchase }} <br />
          <small v-if="nextCharge" class="text-gray03 price-per-user-ps">
            ({{ nextCharge | moment('DD/MM/YYYY') }})
          </small>
        </b>
        <span class="price-per-user text-right">
          <b class="text-initial">R$ {{ total | toCurrency }}</b>
        </span>
      </div>
    </div>

    <div class="text-center mb-3">
      <b-button
        variant="red02"
        @click="updateSubscription()"
        class="pl-5 pr-5 mdi mdi-spin"
        :class="{ 'mdi-loading': loading.origin === 'selfComponent' }"
        :disabled="loading.state || licenses <= activatedUsersCount"
      >
        {{ labels.purchase }}
      </b-button>
    </div>

    <AnnuityBar
      @chosenAnnuity="updateToAnnuity()"
      v-if="billing.period !== periods.ANNUITY && !isBoletoMethod"
      :buttonOptions="buttonOptions"
      :licenses="parseInt(licenses)"
      :annuityPrice="enterpriseAnnuityPrice"
      :activatedUsersCount="activatedUsersCount"
    >
      <span slot="button">
        {{ labels.purchase }}
      </span>
    </AnnuityBar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { updateBoletoLicenses, updateSubscription } from '@/service/account'
import { PERIODS, PLANS_TYPE } from '@/store/modules/account'
import AnnuityBar from '@/components/Shared/AnnuityBar.vue'
import { HIGH } from '@/helpers/messages-priority'

export default {
  components: {
    AnnuityBar,
  },
  beforeMount() {
    try {
      this.loading.state = true
      this.loadPlans()
      this.loadLicenses()
      if (this.isCreditCardMethod) {
        this.loadSimulationData()
      }
    } catch (error) {
      this.$root.$emit('error', error)
    }
    this.loading.state = false
  },
  mounted() {
    this.$root.$emit('track', {
      event: 'Abriu form anual',
      data: {
        category: 'Checkout',
      },
    })
  },
  data() {
    return {
      licenses: 11,
      loading: {
        state: false,
        origin: '',
      },
    }
  },
  watch: {
    availableLicenses(value) {
      this.licenses = value
    },
    licenses(value) {
      if (value < 0) this.licenses = 0
    },
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      companyLicenses: 'company/licenses',
    }),
    ...mapGetters('account', {
      isCreditCardMethod: 'isCreditCardMethod',
      isBoletoMethod: 'isBoletoMethod',
      plans: 'plans',
      billing: 'billing',
    }),
    ...mapGetters('licensesUpdateForm', {
      originalPrice: 'originalPrice',
      partialPrice: 'partialPrice',
      nextCharge: 'nextCharge',
      isAnnuity: 'isAnnuity',
    }),

    labels: function() {
      return Object.assign(
        {},
        this.resources.general.errors,
        this.resources.page.home.accountGeneric,
        this.resources.page.home.licensesForm,
        this.resources.page.home.updateLicenses
      )
    },

    aditionalLicenses: function() {
      return this.licenses > this.availableLicenses
        ? this.licenses - this.availableLicenses
        : false
    },

    total: function() {
      const total = this.licenses * this.originalPrice
      return this.isAnnuity ? total * 12 : total
    },

    activatedUsersCount: function() {
      return this.companyLicenses.activatedUsersCount
    },

    availableLicenses: function() {
      return this.companyLicenses.availableLicenses
    },

    enterprisePrices: function() {
      if (this.plans.length > 0) {
        return this.plans.find(plan => plan.id === PLANS_TYPE.ENTERPRISE).prices
      }
      return []
    },

    enterpriseAnnuityPrice: function() {
      if (this.enterprisePrices.length > 0) {
        return this.enterprisePrices.find(
          ({ period }) => period === this.periods.ANNUITY
        ).pricePerUser
      }
      return 0
    },

    periods: function() {
      return PERIODS
    },
    buttonOptions: function() {
      const { state, origin } = this.loading
      const options = {
        loadingClasses: { classes: 'mdi mdi-spin mdi-loading' },
        disabled: { disabled: true },
        default: {},
      }
      const result = state ? options['disabled'] : options['default']

      return origin !== 'AnnuityBar'
        ? result
        : { ...result, ...options['loadingClasses'] }
    },
  },
  methods: {
    ...mapActions({
      loadSimulationData: 'licensesUpdateForm/loadSimulationData',
      loadLicenses: 'company/loadLicenses',
      loadPlans: 'account/loadPlans',
    }),
    updateToAnnuity: async function() {
      this.updateDefault(
        updateSubscription,
        { licenses: this.licenses, period: this.periods.ANNUITY },
        'AnnuityBar'
      )
    },
    updateSubscription: async function() {
      const apiCall = this.isBoletoMethod
        ? updateBoletoLicenses
        : updateSubscription
      this.updateDefault(apiCall, { licenses: this.licenses }, 'selfComponent')
    },
    updateDefault: async function(apiCall, apiData, origin) {
      this.loading.state = true
      this.loading.origin = origin

      try {
        const result = await apiCall(apiData)

        if (this.isCreditCardMethod) {
          this.loadSimulationData()
        }
        this.$emit('updateSuccess', result)
        this.$root.$emit('track:updateLicenses', {
          event: 'Alter Licenses',
          data: { licenses: this.licenses },
        })
      } catch (error) {
        this.$root.$emit('error', error)
        this.$root.$emit('app:message:show', {
          text: (
            error.response || { data: { message: this.labels.response500 } }
          ).data.message,
          status: 'danger',
          priority: HIGH,
        })
      }

      this.loading.state = false
      this.loading.origin = ''
    },
  },
}
</script>

<style lang="scss">
.licensesform {
  .pointer {
    cursor: pointer;
  }
  .b-form-group.form-group {
    margin-bottom: 0;
  }
}
</style>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';
.licensesform {
  font-size: 16px;
  line-height: initial;

  .resume-aditional-licenses {
    min-width: 100px;
  }

  .text-large {
    font-size: large;
  }

  .text-small {
    font-size: 0.8rem;
  }

  .text-initial {
    font-size: initial;
  }

  .row-column {
    display: flex;
    flex-direction: column;
  }

  .row-centered-items {
    align-items: center;
    color: map-get($theme-colors, 'septenary');
    display: flex;
    justify-content: space-between;
    margin: 1.4rem 0;

    @media (max-width: '425px') {
      &.number-of-licenses {
        flex-direction: column;
        align-items: baseline;
      }
    }

    label {
      font-size: 1.4rem;
      font-weight: 500;
    }

    .licenses {
      background: map-get($theme-colors, 'denary');
      border-radius: 55px !important;
      height: 55px !important;
      padding-right: 20px !important;
      text-align: right;
      width: 250px;

      @media (max-width: '425px') {
        width: 100%;
      }
    }

    &.total-price {
      font-size: 1.5rem;
      font-weight: 500;
      margin: 2rem 0;
    }

    &.align-center {
      align-items: center;
    }

    &.align-baseline {
      align-items: baseline;
      flex-wrap: wrap;

      .price-per-user {
        min-width: 70px;
      }

      .price-per-user-ps {
        width: 100%;
        flex-basis: 100%;
      }
    }
  }

  .btn.btn-red2 {
    border-radius: 55px !important;
    font-weight: 500;
    height: 55px !important;
    line-height: inherit;
    padding-left: 4rem;
    padding-right: 4rem;

    @media (max-width: '425px') {
      width: 100%;
    }
  }
}

.annuity-bar {
  position: absolute;
  left: 0;
}
</style>
